import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/PrefsContext";
import { PageHeader, PrimaryButton, Field, Input, Textarea, Button } from "@/components/ui-kit";
import { LOGO_URL } from "@/components/Logo";
import { Building2, Image, Percent, FileText, Database, Download, Palette, Sun, Moon, Languages } from "lucide-react";

export default function Settings() {
  const { setSettings: setGlobalSettings } = useAuth();
  const { t, theme, setTheme, lang, setLang } = useI18n();
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/settings").then(({ data }) => setForm(data)); }, []);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const payload = { ...form, tax_rate: Number(form.tax_rate) };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      setGlobalSettings(data);
      toast.success(t("Settings saved"));
    } catch { toast.error("Could not save settings"); }
  };

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2MB");
    const reader = new FileReader();
    reader.onload = () => set("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const exportData = async () => {
    try {
      const [clients, invoices, payments, jobs] = await Promise.all([
        api.get("/clients"), api.get("/invoices"), api.get("/payments"), api.get("/jobs"),
      ]);
      const blob = new Blob([JSON.stringify({ clients: clients.data, invoices: invoices.data, payments: payments.data, jobs: jobs.data, exported_at: new Date().toISOString() }, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `magic-touch-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("Backup exported"));
    } catch { toast.error("Could not export data"); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title={t("Settings")} subtitle={t("Manage your company profile, branding, and invoice defaults.")}
        action={<PrimaryButton onClick={save} data-testid="save-settings-button">{t("Save Changes")}</PrimaryButton>} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Appearance: Theme + Language */}
        <Section icon={Palette} title={t("Appearance")}>
          <p className="text-sm text-[#52525B]">{t("Choose how Magic Touch looks and the language of the interface.")}</p>

          <Field label={t("Theme")}>
            <div className="grid grid-cols-2 gap-3">
              <ToggleCard active={theme === "light"} onClick={() => setTheme("light")} icon={Sun} label={t("Light Mode")} testid="theme-light" />
              <ToggleCard active={theme === "dark"} onClick={() => setTheme("dark")} icon={Moon} label={t("Dark Mode")} testid="theme-dark" />
            </div>
          </Field>

          <Field label={t("Language")}>
            <div className="grid grid-cols-2 gap-3">
              <ToggleCard active={lang === "en"} onClick={() => setLang("en")} icon={Languages} label={t("English")} testid="lang-en" />
              <ToggleCard active={lang === "es"} onClick={() => setLang("es")} icon={Languages} label={t("Spanish")} testid="lang-es" />
            </div>
          </Field>
        </Section>

        {/* Company Info */}
        <Section icon={Building2} title={t("Company Information")}>
          <Field label={t("Company Name")}><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="company-name-input" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("Phone")}><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label={t("Email")}><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
          </div>
          <Field label={t("Address")}><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label={t("Website")}><Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} /></Field>
        </Section>

        {/* Logo */}
        <Section icon={Image} title={t("Logo")}>
          <div className="flex items-center gap-4">
            <img src={form.logo_url || LOGO_URL} alt="logo" className="w-24 h-24 rounded-2xl object-cover ring-2 ring-[#D4AF37]/50 shadow" />
            <div>
              <label className="btn-gold-hover inline-flex items-center gap-2 bg-[#5A189A] text-white text-sm font-medium rounded-xl px-4 py-2.5 cursor-pointer">
                <Image size={16} /> {t("Upload Logo")}
                <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="logo-upload-input" />
              </label>
              <p className="text-xs text-[#9b8bb5] mt-2">{t("PNG/JPG up to 2MB. Used across the app, invoices & print.")}</p>
            </div>
          </div>
        </Section>

        {/* Invoice settings */}
        <Section icon={Percent} title={t("Invoice Settings")}>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("Default Tax Rate (%)")}><Input type="number" value={form.tax_rate ?? 0} onChange={(e) => set("tax_rate", e.target.value)} data-testid="tax-rate-input" /></Field>
            <Field label={t("Invoice Prefix")}><Input value={form.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} /></Field>
          </div>
          <Field label={t("Invoice Terms / Footer")}><Textarea rows={3} value={form.invoice_terms || ""} onChange={(e) => set("invoice_terms", e.target.value)} /></Field>
        </Section>

        {/* Theme colors */}
        <Section icon={FileText} title={t("Theme Colors")}>
          <div className="grid grid-cols-3 gap-4">
            <ColorField label={t("Primary")} value={form.primary_color} onChange={(v) => set("primary_color", v)} />
            <ColorField label={t("Secondary")} value={form.secondary_color} onChange={(v) => set("secondary_color", v)} />
            <ColorField label={t("Accent")} value={form.accent_color} onChange={(v) => set("accent_color", v)} />
          </div>
        </Section>

        {/* Backup */}
        <Section icon={Database} title={t("Backup & Export")}>
          <p className="text-sm text-[#52525B]">{t("Download a full JSON backup of your clients, invoices, payments and jobs.")}</p>
          <Button variant="outline" onClick={exportData} data-testid="export-data-button" className="mt-2 w-fit"><Download size={15} className="mr-2" /> {t("Export Data")}</Button>
        </Section>
      </div>
    </div>
  );
}

function ToggleCard({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
        active
          ? "border-[#5A189A] bg-[#5A189A]/10 text-[#5A189A] shadow-sm"
          : "border-[#e7ddf5] text-[#52525B] hover:border-[#5A189A]/40"
      }`}
    >
      <Icon size={18} /> {label}
    </button>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mt-card p-6">
      <h3 className="font-display text-lg font-semibold text-[#121212] mb-4 flex items-center gap-2">
        <Icon size={18} className="text-[#5A189A]" /> {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#5A189A"} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-[#e7ddf5] cursor-pointer" />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1" />
      </div>
    </Field>
  );
}
